import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { HttpService } from './http.service';
import { BrowserService } from './browser.service';

export interface DeliveryAttachedCompany {
  name?: string;
  type?: 'independent' | 'courier_partner' | 'merchant_bazaar' | 'seller_affiliated' | 'other';
  employeeId?: string;
  contactPhone?: string;
  contactEmail?: string;
  address?: string;
  city?: string;
  website?: string;
  verified?: boolean;
}

export interface DeliveryEmergencyContact {
  name?: string;
  phone?: string;
  relation?: string;
}

export interface DeliveryAgentProfile {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  avatar?: string;
  vehicleType?: string;
  vehiclePlate?: string;
  bio?: string;
  licenseNumber?: string;
  nationalId?: string;
  serviceAreas?: string[];
  attachedCompany?: DeliveryAttachedCompany;
  emergencyContact?: DeliveryEmergencyContact;
  status: string;
  isOnline?: boolean;
  createdAt?: string;
  settings?: {
    notificationsEnabled?: boolean;
    emailNotifications?: boolean;
    smsNotifications?: boolean;
    shareLocationWhileDelivering?: boolean;
    autoGoOnlineWhenTracking?: boolean;
    geofenceEnabled?: boolean;
    geofenceRadiusMeters?: number;
    requirePhotoOnDelivery?: boolean;
    soundAlerts?: boolean;
    autoAcceptAssignedOrders?: boolean;
    codReminderEnabled?: boolean;
    preferredLanguage?: string;
    workingHoursStart?: string;
    workingHoursEnd?: string;
    locationSyncIntervalSec?: number;
  };
  mobileAppInstalled?: boolean;
  courierCredentials?: unknown[];
}

const TOKEN_KEY = 'delivery_token';
const AGENT_KEY = 'delivery_agent';

@Injectable({ providedIn: 'root' })
export class DeliveryAuthService {
  private browser = inject(BrowserService);
  private agentSubject = new BehaviorSubject<DeliveryAgentProfile | null>(this.readStoredAgent());
  agent$ = this.agentSubject.asObservable();

  constructor(private http: HttpService) {}

  private readStoredAgent(): DeliveryAgentProfile | null {
    try {
      const raw = this.browser.getLocalStorage(AGENT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  getToken(): string | null {
    return this.browser.getLocalStorage(TOKEN_KEY);
  }

  /** Drop expired or non-delivery JWT so login page is not skipped by guest guard. */
  pruneInvalidSession(): void {
    if (!this.getToken()) return;
    if (!this.isLoggedIn()) {
      this.logout();
    }
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64)) as { exp?: number; deliveryAgent?: boolean };
      if (payload.deliveryAgent !== true) {
        return false;
      }
      if (payload.exp && payload.exp * 1000 <= Date.now()) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  getAgent(): DeliveryAgentProfile | null {
    return this.agentSubject.value;
  }

  login(email: string, password: string): Observable<{ success: boolean; token: string; agent: DeliveryAgentProfile }> {
    const payload = {
      email: email.trim().toLowerCase(),
      password,
    };
    return this.http
      .post<{ success: boolean; token: string; agent: DeliveryAgentProfile }>(
        'delivery-profile/auth/login',
        payload,
        { includeSellingMode: false, includeLang: false },
      )
      .pipe(
        map((res) => {
          if (!res?.token) {
            throw { status: 500, message: 'Login response missing token' };
          }
          this.persistSession(res.token, res.agent);
          return res;
        }),
      );
  }

  register(payload: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
  }): Observable<{ success: boolean; token: string; agent: DeliveryAgentProfile }> {
    return this.http
      .post<{ success: boolean; token: string; agent: DeliveryAgentProfile }>(
        'delivery-profile/auth/register',
        payload,
        { includeSellingMode: false, includeLang: false },
      )
      .pipe(tap((res) => this.persistSession(res.token, res.agent)));
  }

  logout(): void {
    this.browser.removeLocalStorage(TOKEN_KEY);
    this.browser.removeLocalStorage(AGENT_KEY);
    this.agentSubject.next(null);
  }

  refreshAgent(agent: DeliveryAgentProfile): void {
    const token = this.getToken();
    if (token) this.persistSession(token, agent);
  }

  private persistSession(token: string, agent: DeliveryAgentProfile | null | undefined): void {
    try {
      this.browser.setLocalStorage(TOKEN_KEY, token);
      if (agent) {
        this.browser.setLocalStorage(AGENT_KEY, JSON.stringify(agent));
        this.agentSubject.next(agent);
      }
      this.browser.setSessionStorage('lastLoginTime', String(Date.now()));
      this.browser.setSessionStorage('deliveryLastLoginTime', String(Date.now()));
    } catch {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(TOKEN_KEY, token);
        if (agent) {
          localStorage.setItem(AGENT_KEY, JSON.stringify(agent));
          this.agentSubject.next(agent);
        }
      }
    }
  }
}
